/**
 * Truncates an email address by shortening the username, ensuring equal characters
 * from the start and end of the username are kept, with "..." in between. The total
 * length of the email is capped by `maxLength` (default: 25 characters).
 *
 * @param email {string} The email address to truncate
 * @param maxLength {number} The maximum length of the returned email string, including the "@" and domain (default: 25)
 * @returns {string} Truncated email with equal characters from the beginning and end of the username, keeping the domain intact
 *
 * @example
 *   truncateEmail('t-0181af64dd8246d@example.com') // => 't-0...6d@example.com'
 *   truncateEmail('user@example.com', 10) // => 'u...@example.com'
 *   truncateEmail('longusername@example.com', 20) // => 'long...name@example.com'
 */
export function truncateEmail(email: string, maxLength: number = 25): string {
  if (email.length <= maxLength) return email

  const [name, domain] = email.split("@")
  const availableLength = maxLength - domain.length - 4 // 4 is for "..." and "@"

  // Ensure we have enough space for at least one character on each side
  if (availableLength <= 0) return `${name.slice(0, 1)}...@${domain}`

  const halfLength = Math.floor(availableLength / 2)

  return `${name.slice(0, halfLength)}...${name.slice(-halfLength)}@${domain}`
}
